import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { Tabs, Form, type TabsProps } from "antd";
import { observer } from "mobx-react";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { Button } from "Components/General/Button";
import { documentsSearchStore } from "../../Stores/DocumentsSearchStore";
import { RouterStore } from "Config/CustomBrowserRouter";
import { projectsDataStore, userInfoStore } from "Stores";
import { documentsTableStore } from "../../Stores/DocumentsTableStore";
import { StandardFields } from "./StandardFields";
import { MetadataFields } from "./MetadataFields";
import { metadataStore } from "../../Stores/MetadataStore";
import { getUrlParams } from "utils/getUrlParams";
import { FormProviderProps } from "antd/lib/form/context";
import { VirusFields } from "./VirusFields";
import { FullTextSearchFields } from "./FullTextSearchFields";

// Used to avoid "You cannot set a form field before rendering a field associated with the value." error
// Basically it's a placeholder for "metadata" field which is conditionally available in form request body.
class Blank extends React.Component {
  render() {
    return null;
  }
}

// tslint:disable-next-line: max-line-length
export interface DocumentsSearchFormProps extends WithTranslation, ModalComponentProps {
  hideSubmitButton?: boolean;
  initialData?: API_DocSearchReq;
  disabled?: boolean;
  updateSearchbarValue?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref?: any;
}

interface DocumentsSearchFormState {
  activeKey: string;
  metadataDisabled: boolean;
}

export const DocumentsSearchForm = withTranslation(I18N_NS.PROJECT, { withRef: true })(
  observer(
    class DocumentsSearchFormClass extends React.Component<DocumentsSearchFormProps, DocumentsSearchFormState> {
      changedFormData: API_DocSearchReq = {};
      constructor(props: DocumentsSearchFormProps) {
        super(props);

        this.state = {
          activeKey: "1",
          metadataDisabled: false,
        };
      }

      componentDidMount() {
        if (projectsDataStore.functions.includes("METADATA")) {
          metadataStore.fetch();
        } else {
          this.setState({ metadataDisabled: true });
        }
      }

      componentWillUnmount() {
        metadataStore.reset();
      }

      get searchFolderId() {
        const { folderId } = getUrlParams(window.location.search);
        return +folderId;
      }

      handleSubmitSearch = async (postObject: API_SearchQuery) => {
        try {
          const folderId = this.searchFolderId || documentsTableStore.currentFolderId;
          // update searchbar if it was changed
          if (
            postObject.fileName !== documentsSearchStore.searchReqQueries.fileName &&
            this.props.updateSearchbarValue
          ) {
            this.props.updateSearchbarValue();
          }
          // make sure that the switcher is in sync with the detail search form
          if (postObject.returnDeleted !== documentsTableStore.showDeleted) {
            documentsTableStore.toggleShowDeleted(false);
          }
          await documentsSearchStore.submitSearch({ ...postObject, folderId });
        } catch (error) {
          throw error;
        }
      };

      handleFinish = async (name, { forms }) => {
        try {
          const { standardFields, metadataFields, virusFields, fullTextSearchFields } = forms;
          const formData = {
            ...standardFields.getFieldsValue(),
            ...fullTextSearchFields.getFieldsValue(),
            metadata: metadataFields?.getFieldsValue(),
            ...virusFields?.getFieldsValue(),
          };
          const searchFolderId = this.searchFolderId || documentsTableStore.currentFolderId;
          this.props.hideModal();
          RouterStore.push(
            `/project/${projectsDataStore.projectId}/documents/docs/search-results?folderId=${searchFolderId}`
          );
          await this.handleSubmitSearch(formData);
        } catch (err) {
          throw err;
        }
      };

      persistFormData: FormProviderProps["onFormChange"] = (formName, { forms }) => {
        const { standardFields, metadataFields, virusFields } = forms;
        this.changedFormData = {
          ...standardFields.getFieldsValue(),
          metadata: metadataFields?.getFieldsValue(),
          ...virusFields?.getFieldsValue(),
        };
      };

      handleTabChange = (activeKey: string) => {
        this.setState({ activeKey });
      };

      get tabsItems() {
        const { t, initialData } = this.props;
        const { metadataDisabled } = this.state;
        const showProblematicFilesTab = projectsDataStore.isAdmin || userInfoStore.isAdmin;
        const items: NonNullable<TabsProps["items"]> = [
          {
            key: "1",
            label: t("Standard fields"),
            children: <StandardFields initialData={initialData} />,
            disabled: false,
            forceRender: true,
          },
          {
            key: "2",
            label: t("Full-text search"),
            children: <FullTextSearchFields initialData={initialData} />,
            disabled: false,
            forceRender: true,
          },
          {
            key: "3",
            label: t("Metadata"),
            children: <MetadataFields initialData={initialData?.metadata} />,
            disabled: metadataDisabled,
            forceRender: true,
          },
        ];
        if (showProblematicFilesTab) {
          items.push({
            key: "4",
            label: t("Problematic files"),
            children: <VirusFields />,
            disabled: false,
            forceRender: true,
          });
        }
        return items;
      }

      render() {
        const { t, hideSubmitButton } = this.props;
        const { metadataDisabled } = this.state;

        return (
          <Form.Provider onFormFinish={this.handleFinish} onFormChange={this.persistFormData}>
            <Tabs activeKey={this.state.activeKey} onChange={this.handleTabChange} items={this.tabsItems} />

            {!metadataDisabled && (
              <Form.Item noStyle name="metadata">
                <Blank />
              </Form.Item>
            )}
            {hideSubmitButton ? null : (
              <Form>
                <div className="text-right">
                  <Button type="primary" htmlType="submit">
                    {t("Search")}
                  </Button>
                </div>
              </Form>
            )}
          </Form.Provider>
        );
      }
    }
  )
);

// export const DocumentsSearchForm = withTranslation(I18N_NS.PROJECT)(observer(
//   DocumentsSearchFormClass
// ));
